import '@/global.css';
import {
  Inter_100Thin,
  Inter_200ExtraLight,
  Inter_300Light,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  Inter_800ExtraBold,
  Inter_900Black,
} from '@expo-google-fonts/inter';
import {
  Poppins_100Thin,
  Poppins_200ExtraLight,
  Poppins_300Light,
  Poppins_400Regular,
  Poppins_500Medium,
  Poppins_600SemiBold,
  Poppins_700Bold,
  Poppins_800ExtraBold,
  Poppins_900Black,
} from '@expo-google-fonts/poppins';
import { tx } from '@transifex/native';
import { useFonts } from 'expo-font';
import { SplashScreen, Stack } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useLayoutEffect, useState } from 'react';
import { Platform } from 'react-native';
import 'react-native-reanimated';

import { AnalyticsProvider } from '@/modules/analytics';
import { configureDotLottieWasm } from '@/modules/lottie';
import { OnboardingStepsProvider, RulesEngineProvider } from '@/modules/rulesEngine';
import { AppThemeProvider } from '@/modules/theme';
import { useLanguageSetting } from '@/modules/transifex';
import { LoadingScreen } from '@/modules/ui';

SplashScreen.preventAutoHideAsync();

tx.init({
  token: process.env.EXPO_PUBLIC_TRANSIFEX_TOKEN || '',
});

// Point the dotLottie web player at our self-hosted WASM before any screen (and
// thus any LottieView) mounts — see src/modules/lottie. No-op on native.
configureDotLottieWasm();

export default function RootLayout() {
  const [isWebMounted, setIsWebMounted] = useState(Platform.OS !== 'web');
  const [fontsLoaded] = useFonts({
    Inter_100Thin,
    Inter_200ExtraLight,
    Inter_300Light,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    Inter_800ExtraBold,
    Inter_900Black,
    Poppins_100Thin,
    Poppins_200ExtraLight,
    Poppins_300Light,
    Poppins_400Regular,
    Poppins_500Medium,
    Poppins_600SemiBold,
    Poppins_700Bold,
    Poppins_800ExtraBold,
    Poppins_900Black,
  });

  const { ready } = useLanguageSetting();

  useLayoutEffect(() => {
    if (!isWebMounted && Platform.OS === 'web') {
      setIsWebMounted(true);
      return;
    }

    if (isWebMounted && fontsLoaded && ready) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, isWebMounted, ready]);

  if (Platform.OS === 'web') {
    if (!isWebMounted) {
      return <LoadingScreen />;
    }

    if (!fontsLoaded || !ready) {
      return <ThemedLoadingScreen />;
    }
  } else if (!fontsLoaded || !ready) {
    return <ThemedLoadingScreen />;
  }

  return (
    <AnalyticsProvider>
      <AppThemeProvider>
        <RulesEngineProvider>
          <OnboardingStepsProvider>
            <Stack screenOptions={{ headerShown: false }} />
            <StatusBar style='auto' />
          </OnboardingStepsProvider>
        </RulesEngineProvider>
      </AppThemeProvider>
    </AnalyticsProvider>
  );
}

function ThemedLoadingScreen() {
  return (
    <AppThemeProvider>
      <LoadingScreen />
    </AppThemeProvider>
  );
}
